// src/components/EarningsTrend.js
import React, { useEffect, useState } from 'react';

const EarningsTrend = ({ stockSymbol, companyName }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!stockSymbol) return;

    const fetchEarnings = async () => {
      setLoading(true);
      try {
        const resp = await fetch(
          `https://metrics-8.onrender.com/api/stock/${encodeURIComponent(stockSymbol)}/quarters?quarters=6`
        );
        if (resp.ok) {
          const json = await resp.json();
          setData(json);
        }
      } catch (e) {
        console.warn('[EarningsTrend] fetch error:', e);
      } finally {
        setLoading(false);
      }
    };

    fetchEarnings();
  }, [stockSymbol]);

  if (!stockSymbol) return null;
  if (loading) return <div className="text-sm text-gray-500">Loading earnings data…</div>;
  if (!data || !data.byDate) return null;

  // Extract EPS + revenue data per quarter (chronological order)
  const { quarters = [], dates = [], byDate = {} } = data;
  const chronological = [...dates].reverse();
  const chronoLabels = [...quarters].reverse();

  const epsData = chronological.map((d) => {
    const income = byDate[d]?.income;
    return income?.epsDiluted ?? income?.earningsPerShare ?? null;
  });

  const revenueData = chronological.map((d) => {
    const income = byDate[d]?.income;
    return income?.revenue ?? null;
  });

  const validEps = epsData.filter((v) => v != null);
  if (validEps.length < 2) {
    return (
      <div className="card p-5">
        <h3 className="text-base font-semibold text-gray-800 mb-2">Earnings Trend</h3>
        <p className="text-sm text-gray-500">Not enough quarterly data yet. Refresh to populate.</p>
      </div>
    );
  }

  // Calculate EPS growth
  const latestEps = validEps[validEps.length - 1];
  const previousEps = validEps[validEps.length - 2];
  const epsGrowth = previousEps !== 0
    ? ((latestEps - previousEps) / Math.abs(previousEps) * 100).toFixed(1)
    : null;

  // Find max EPS for bar scaling
  const maxEps = Math.max(...validEps.map(Math.abs), 0.01);

  // Format revenue in billions/millions
  const formatRevenue = (val) => {
    if (val == null) return '—';
    if (Math.abs(val) >= 1e9) return `$${(val / 1e9).toFixed(1)}B`;
    if (Math.abs(val) >= 1e6) return `$${(val / 1e6).toFixed(0)}M`;
    return `$${val.toLocaleString()}`;
  };

  return (
    <div className="card p-5">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-base font-semibold text-gray-800">Earnings Trend</h3>
        {epsGrowth != null && (
          <span className={`text-xs font-semibold px-2 py-1 rounded-full ${
            parseFloat(epsGrowth) >= 0
              ? 'bg-green-100 text-green-700'
              : 'bg-red-100 text-red-700'
          }`}>
            {parseFloat(epsGrowth) >= 0 ? '↑' : '↓'} {Math.abs(epsGrowth)}% QoQ
          </span>
        )}
      </div>

      {/* EPS bar chart */}
      <div className="flex items-end gap-1.5 h-24 mb-3">
        {epsData.map((eps, i) => {
          if (eps == null) return (
            <div key={i} className="flex flex-col items-center flex-1">
              <div className="w-full bg-gray-100 rounded-t-md h-2" />
              <span className="text-[9px] text-gray-400 mt-1">{chronoLabels[i]?.split('-')[1] || ''}</span>
            </div>
          );

          const height = (Math.abs(eps) / maxEps) * 100;
          const isPositive = eps >= 0;

          return (
            <div key={i} className="flex flex-col items-center flex-1">
              <span className="text-[10px] font-semibold text-gray-700 mb-0.5">
                ${eps.toFixed(2)}
              </span>
              <div
                className={`w-full rounded-t-md ${isPositive ? 'bg-brand-500' : 'bg-red-400'}`}
                style={{ height: `${Math.max(height, 6)}%` }}
              />
              <span className="text-[9px] text-gray-500 mt-1">
                {chronoLabels[i]?.replace(/^\d{4}-/, '') || ''}
              </span>
            </div>
          );
        })}
      </div>

      {/* Revenue row */}
      <div className="border-t border-gray-100 pt-3">
        <div className="flex items-center justify-between text-xs text-gray-500 mb-1">
          <span>Revenue by quarter</span>
        </div>
        <div className="flex gap-1.5">
          {revenueData.map((rev, i) => (
            <div key={i} className="flex-1 text-center">
              <div className="text-[10px] font-medium text-gray-700">
                {formatRevenue(rev)}
              </div>
              <div className="text-[9px] text-gray-400">
                {chronoLabels[i]?.replace(/^\d{4}-/, '') || ''}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default EarningsTrend;
